import { Box, Flex } from "rebass/styled-components";
import useTranslation from "next-translate/useTranslation";

import Heading from "../common/Heading";
import Select from "../common/Select";
import ErrorProp from "../common/ErrorProp";
import {
  useSitterContactDispatch,
  useSitterContactState
} from "./SitterContactContext";
import { TimeSelectorMode } from "../../enums";
import { getTimeFilterModeForCountryServiceType } from "../../lib/utils";
import { Label } from "@rebass/forms/styled-components";
import {
  TimeSelectorValue,
  StartTime,
  DropOffPickupTime,
  DatesSelectorValue
} from "../../interfaces";
import { useEffect } from "react";

interface Props {
  kind: DatesSelectorValue["kind"];
  error: ErrorProp;
}

export const getDefaultTimeSelector = (serviceType): TimeSelectorValue => {
  return {
    kind: serviceType.alwaysPickUpDropOff ? "DropOffPickupTime" : "StartTime",
    startTime: { label: "-- : --", value: "99" },
    dropOff: { label: "-- : --", value: "99" },
    pickUp: { label: "-- : --", value: "99" }
  };
};

function TimesSelector({ kind, error }: Props) {
  const { t } = useTranslation("sittercontact");

  const sitterContactDispatch = useSitterContactDispatch();
  const sitterContactState = useSitterContactState();

  const { countryServiceType, timeSelectorValue } = sitterContactState;

  const availableTimes = [
    { label: "08:00 - 09:00", value: "8" },
    { label: "09:00 - 10:00", value: "9" },
    { label: "10:00 - 11:00", value: "10" },
    { label: "11:00 - 12:00", value: "11" },
    { label: "12:00 - 13:00", value: "12" },
    { label: "13:00 - 14:00", value: "13" },
    { label: "14:00 - 15:00", value: "14" },
    { label: "15:00 - 16:00", value: "15" },
    { label: "16:00 - 17:00", value: "16" },
    { label: "17:00 - 18:00", value: "17" },
    { label: "18:00 - 19:00", value: "18" },
    { label: "19:00 - 20:00", value: "19" },
    { label: "20:00 - 21:00", value: "20" },
    { label: "21:00 - 22:00", value: "21" },
    { label: "22:00 - 23:00", value: "22" },
    { label: "23:00 - 00:00", value: "23" },
    { label: "00:00 - 01:00", value: "0" },
    { label: "01:00 - 02:00", value: "1" },
    { label: "02:00 - 03:00", value: "2" },
    { label: "03:00 - 04:00", value: "3" },
    { label: "04:00 - 05:00", value: "4" },
    { label: "05:00 - 06:00", value: "5" },
    { label: "06:00 - 07:00", value: "6" },
    { label: "07:00 - 08:00", value: "7" }
  ];

  const getNextAvailableTime = (timeValue: string) => {
    let nextTime;
    if (timeValue !== "23") {
      nextTime = availableTimes.find(
        timeObj => +timeObj.value === +timeValue + 1
      );
    }
    return nextTime || availableTimes[15];
  };

  useEffect(() => {
    let payload: DropOffPickupTime = {
      ...timeSelectorValue,
      kind: "DropOffPickupTime",
      dropOff: getDefaultTimeSelector(countryServiceType.serviceType).dropOff,
      pickUp: getDefaultTimeSelector(countryServiceType.serviceType).pickUp,
      startTime: getDefaultTimeSelector(countryServiceType.serviceType)
        .startTime
    };
    sitterContactDispatch({
      type: "changeTimeSelectorValue",
      payload: {
        timeSelectorValue: payload
      }
    });
  }, [kind]);

  const getAvailablePickUpTimes = () => {
    if (kind === "multipleDates") {
      return availableTimes.filter(timeObj => {
        const dropOff = timeSelectorValue.dropOff?.value;
        if (dropOff === "23") return +timeObj.value === +dropOff;
        return dropOff ? +timeObj.value > +dropOff : true;
      });
    } else {
      return availableTimes;
    }
  };

  type TimeObject = {
    dropOff?: TimeSelectorValue["dropOff"];
    pickUp?: TimeSelectorValue["pickUp"];
  };

  const onDropOffPickupTimeChange = (time: TimeObject) => {
    let payload: DropOffPickupTime;
    const pickUpSelector = timeSelectorValue.pickUp?.value || "0";
    if (
      time.dropOff &&
      kind === "multipleDates" &&
      +time.dropOff.value > +pickUpSelector
    ) {
      payload = {
        ...timeSelectorValue,
        kind: "DropOffPickupTime",
        pickUp: getNextAvailableTime(time.dropOff.value),
        ...time
      };
    } else {
      payload = {
        ...timeSelectorValue,
        kind: "DropOffPickupTime",
        ...time
      };
    }

    sitterContactDispatch({
      type: "changeTimeSelectorValue",
      payload: {
        timeSelectorValue: payload
      }
    });
  };

  const onStartTimeChange = (time: TimeSelectorValue["startTime"]) => {
    const payload: StartTime = {
      kind: "StartTime",
      startTime: time,
      dropOff: timeSelectorValue.dropOff,
      pickUp: timeSelectorValue.pickUp
    };
    sitterContactDispatch({
      type: "changeTimeSelectorValue",
      payload: {
        timeSelectorValue: payload
      }
    });
  };

  const renderTimePicker = () => {
    switch (getTimeFilterModeForCountryServiceType(countryServiceType)) {
      case TimeSelectorMode.DROPOFF_PICKUP_TIME:
        return (
          <Flex
            sx={{
              flexWrap: "wrap",
              justifyContent: "space-between",
              flexDirection: ["column", "column", "row"]
            }}
            data-test="dropoff-pickup"
          >
            <Box
              sx={{
                flexGrow: [1, 0.4, 0.4]
              }}
            >
              <Heading as="h5">{t("dropOff")}</Heading>
              <Flex py={1}>
                <Box width={1}>
                  <Select
                    error={error}
                    icon="clock"
                    placeholder={t("from")}
                    value={timeSelectorValue.dropOff}
                    onChange={value => {
                      onDropOffPickupTimeChange({
                        dropOff: value
                      });
                    }}
                    options={availableTimes.reduce(
                      (result, currentValue, index) => {
                        result[index] = {
                          value: currentValue.value,
                          label: currentValue.label
                        };
                        return result;
                      },
                      []
                    )}
                  ></Select>
                </Box>
              </Flex>
            </Box>
            <Box
              sx={{
                flexGrow: [1, 0.4, 0.4]
              }}
            >
              <Heading as="h5">{t("pickUp")}</Heading>
              <Flex py={1}>
                <Box width={1}>
                  <Select
                    error={error}
                    icon="clock"
                    value={timeSelectorValue.pickUp}
                    onChange={value => {
                      onDropOffPickupTimeChange({
                        pickUp: value
                      });
                    }}
                    options={getAvailablePickUpTimes().reduce(
                      (result, currentValue, index) => {
                        result[index] = {
                          value: currentValue.value,
                          label: currentValue.label
                        };
                        return result;
                      },
                      []
                    )}
                    placeholder={t("from")}
                  ></Select>
                </Box>
              </Flex>
            </Box>
          </Flex>
        );
      case TimeSelectorMode.START_TIME:
        return (
          <Box data-test="starttime">
            <Heading as="h5" pt={1}>
              {t("pickUp")}
            </Heading>
            <Flex py={1} width={[1, 1 / 2]} pr={[1, 4]}>
              <Box width={1}>
                <Select
                  error={error}
                  icon="clock"
                  value={timeSelectorValue.startTime}
                  onChange={value => {
                    onStartTimeChange(value);
                  }}
                  placeholder={t("from")}
                  options={availableTimes.reduce(
                    (result, currentValue, index) => {
                      result[index] = {
                        value: currentValue.value,
                        label: currentValue.label
                      };
                      return result;
                    },
                    []
                  )}
                ></Select>
              </Box>
            </Flex>
          </Box>
        );
    }
  };

  return (
    <Box>
      {renderTimePicker()}
      {error.hasError && (
        <Label
          data-test="dates-selector-error"
          sx={{
            color: "errorRed"
          }}
        >
          {error.message}
        </Label>
      )}
    </Box>
  );
}

export default TimesSelector;
